import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@medusajs/ui";
import { Form } from "@repo/design-system";
import { memo } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { CheckoutAddressFormData } from "../validations";

export interface ContactInformationProps {
  /** React Hook Form instance */
  form: UseFormReturn<CheckoutAddressFormData>;
  /** Whether the form is disabled (e.g., during submission) */
  disabled?: boolean;
  /** Whether data is loading (shows skeleton) */
  isLoading?: boolean;
}

/**
 * Contact information form for checkout
 *
 * Two-column layout with:
 * - Email address (pre-filled from profile)
 * - Phone number (pre-filled from default address)
 *
 * @example
 * ```tsx
 * <ContactInformation form={form} disabled={isSubmitting} />
 * ```
 */
export const ContactInformation = memo(function ContactInformation({
  form,
  disabled = false,
  isLoading = false
}: ContactInformationProps) {
  // Loading skeleton state
  if (isLoading) {
    return (
      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        role="status"
        aria-label="Loading contact information"
      >
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {/* Email Field */}
      <Form.Field
        control={form.control}
        name="email"
        render={({ field }) => (
          <Form.Item>
            <Form.Label>Confirmation email</Form.Label>
            <Form.Control>
              <Input
                // Not `type="email"`: the Input paints its error ring on the
                // CSS `:invalid` state, which a half-typed address is in. The
                // form schema validates; `inputMode` keeps the mobile keyboard.
                inputMode="email"
                autoComplete="email"
                // An address longer than the field would otherwise be cut off
                // mid-character at the edge when the field is not focused;
                // ellipsize it instead (focused, it scrolls with the caret).
                className="text-ellipsis"
                placeholder="your@email.com"
                disabled={disabled}
                {...field}
              />
            </Form.Control>
            <Form.ErrorMessage />
          </Form.Item>
        )}
      />
      <Form.Field
        control={form.control}
        name="phone"
        render={({ field }) => (
          <Form.Item>
            <Form.Label>Phone number</Form.Label>
            <Form.Control>
              <Input
                type="tel"
                autoComplete="tel"
                placeholder="+61 412 345 678"
                disabled={disabled}
                {...field}
              />
            </Form.Control>
            <Form.ErrorMessage />
          </Form.Item>
        )}
      />
    </div>
  );
});
